import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
} from '@confluence/content-unified-query';

type TaskStatusUpdate = {
	localId: string;
	state: string;
};

export type TaskStatusPatch = {
	contentId?: string;
	contentType?: string | null;
	spaceKey?: string | null;
	taskStatusUpdates: TaskStatusUpdate[];
};

type AdfNode = {
	type?: string;
	attrs?: { localId?: string; state?: string };
	content?: AdfNode[];
};

// All @include flags are off so the read only requires body (an unconditional field) and can't
// miss on an experiment-gated field, and the write can't drop sibling fields it didn't select.
type PatchVariables = {
	contentId: string;
	spaceKey?: string | null;
	isSSR: false;
	useNewContentTopper: false;
	preloadLabels: false;
	includeAgentTrustReceiptData: false;
	fetchAgentSessionsMarker: false;
	agentSessionsPropertyKey: '';
};

type ContentBodyCacheShape = {
	content?: {
		nodes?: Array<{
			body?: {
				dynamic?: { representation?: string; value?: string | null } | null;
			} | null;
		} | null> | null;
	} | null;
};

const applyTaskStates = (root: AdfNode, states: Map<string, string>): boolean => {
	let changed = false;
	const stack: AdfNode[] = [root];
	while (stack.length > 0) {
		const node = stack.pop();
		if (!node || typeof node !== 'object') {
			continue;
		}
		const localId = node.type === 'taskItem' ? node.attrs?.localId : undefined;
		if (localId && states.has(localId)) {
			const nextState = states.get(localId);
			if (node.attrs && nextState && node.attrs.state !== nextState) {
				node.attrs.state = nextState;
				changed = true;
			}
		}
		if (Array.isArray(node.content)) {
			stack.push(...node.content);
		}
	}
	return changed;
};

export const patchTaskStatusInContentCache = ({
	contentId,
	contentType,
	spaceKey,
	taskStatusUpdates,
}: {
	contentId?: string;
	contentType?: string | null;
	spaceKey?: string | null;
	taskStatusUpdates: TaskStatusUpdate[];
}): void => {
	if (!contentId || taskStatusUpdates.length === 0) {
		return;
	}

	const query =
		contentType === 'blogpost' ? ContentBlogUnifiedQueryArtifact : ContentUnifiedQueryArtifact;
	const variables: PatchVariables = {
		contentId,
		spaceKey,
		isSSR: false,
		useNewContentTopper: false,
		preloadLabels: false,
		includeAgentTrustReceiptData: false,
		fetchAgentSessionsMarker: false,
		agentSessionsPropertyKey: '',
	};

	try {
		const data = readQueryInterop<ContentBodyCacheShape, PatchVariables>({
			query,
			variables,
		});
		const content = data?.content;
		const nodes = content?.nodes;
		const dynamic = nodes?.[0]?.body?.dynamic;
		if (!dynamic || dynamic.representation !== 'atlas_doc_format' || !dynamic.value) {
			return;
		}

		const adf: AdfNode = JSON.parse(dynamic.value);
		const states = new Map(taskStatusUpdates.map(({ localId, state }) => [localId, state]));
		if (!applyTaskStates(adf, states)) {
			return;
		}

		const patchedValue = JSON.stringify(adf);
		writeQueryInterop<ContentBodyCacheShape, PatchVariables>({
			query,
			variables,
			data: {
				...data,
				content: {
					...content,
					nodes: nodes.map((node, index) =>
						index === 0 && node
							? {
									...node,
									body: {
										...node.body,
										dynamic: { ...node.body?.dynamic, value: patchedValue },
									},
								}
							: node,
					),
				},
			},
		});
	} catch {
		// Best-effort: a missing/unreadable cache entry must not throw while leaving the page.
	}
};
